import React, { useCallback, useEffect, useState } from 'react';
import {
  FiArrowLeft,
  FiCheck,
  FiClock,
  FiInbox,
  FiMail,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUser,
} from 'react-icons/fi';
import { api } from '../services/api';
import './AdminContactMessages.css';

const STATUSES = {
  new: { label: 'جدید', tone: 'blue' },
  in_review: { label: 'در حال بررسی', tone: 'amber' },
  replied: { label: 'پاسخ داده‌شده', tone: 'green' },
  closed: { label: 'بسته‌شده', tone: 'gray' },
};

const TOPICS = {
  website: 'طراحی و توسعه وب‌سایت',
  'ui-ux': 'طراحی رابط و تجربه کاربری',
  plugin: 'توسعه افزونه اختصاصی',
  branding: 'هویت بصری و طراحی لوگو',
  support: 'پشتیبانی فنی',
  collaboration: 'همکاری با هریاد',
  other: 'سایر موضوعات',
};

const BUDGETS = {
  'under-20': 'کمتر از ۲۰ میلیون تومان',
  '20-50': '۲۰ تا ۵۰ میلیون تومان',
  '50-100': '۵۰ تا ۱۰۰ میلیون تومان',
  'over-100': 'بیشتر از ۱۰۰ میلیون تومان',
  unsure: 'نیازمند مشاوره',
};

const CONTACT_METHODS = { email: 'ایمیل', phone: 'تماس تلفنی', whatsapp: 'واتساپ' };
const formatDate = (value) => value ? new Date(value).toLocaleString('fa-IR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const AdminContactMessages = ({ onCountChange }) => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, unreadCount: 0, page: 1, pages: 1 });
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editor, setEditor] = useState({ status: 'new', adminNote: '' });
  const [notice, setNotice] = useState({ type: '', text: '' });

  const fetchMessages = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(page), limit: '20' });
      if (filters.status) query.set('status', filters.status);
      if (filters.search.trim()) query.set('search', filters.search.trim());
      const response = await api.get(`/api/contact-messages?${query.toString()}`);
      const data = response.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setMeta({ total: data.total || 0, unreadCount: data.unreadCount || 0, page: data.page || 1, pages: data.pages || 1 });
      onCountChange?.(data.unreadCount || 0);
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'دریافت پیام‌ها انجام نشد.' });
    } finally {
      setLoading(false);
    }
  }, [filters.search, filters.status, onCountChange, page]);

  useEffect(() => {
    const timer = setTimeout(() => fetchMessages(), filters.search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [fetchMessages, filters.search]);

  const openMessage = async (message) => {
    setDetailLoading(true);
    setNotice({ type: '', text: '' });
    try {
      const response = await api.get(`/api/contact-messages/${message._id}`);
      setSelected(response.data);
      setEditor({ status: response.data.status, adminNote: response.data.adminNote || '' });
      setItems((current) => current.map((item) => item._id === message._id ? { ...item, readAt: response.data.readAt } : item));
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'نمایش جزئیات پیام انجام نشد.' });
    } finally {
      setDetailLoading(false);
    }
  };

  const saveMessage = async () => {
    if (!selected) return;
    setSaving(true);
    setNotice({ type: '', text: '' });
    try {
      const response = await api.put(`/api/contact-messages/${selected._id}`, editor);
      setSelected(response.data);
      setItems((current) => current.map((item) => item._id === response.data._id ? response.data : item));
      setNotice({ type: 'success', text: 'وضعیت و یادداشت پیام ذخیره شد.' });
      fetchMessages({ silent: true });
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'ذخیره تغییرات انجام نشد.' });
    } finally {
      setSaving(false);
    }
  };

  const deleteMessage = async () => {
    if (
      !selected
      || deleting
      || !window.confirm(
        `پیام «${selected.name}» با کد ${selected.ticketNumber} برای همیشه حذف شود؟\n\nاین عملیات قابل بازگشت نیست.`
      )
    ) return;

    const deletedId = selected._id;
    const wasNew = selected.status === 'new';
    const isLastItemOnPage = items.length === 1;
    setDeleting(true);
    setNotice({ type: '', text: '' });
    try {
      const response = await api.delete(`/api/contact-messages/${deletedId}`);
      setItems((current) => current.filter((item) => item._id !== deletedId));
      setSelected(null);
      setEditor({ status: 'new', adminNote: '' });
      setMeta((current) => ({
        ...current,
        total: Math.max(0, current.total - 1),
        unreadCount: Math.max(0, current.unreadCount - (wasNew ? 1 : 0)),
      }));
      onCountChange?.(Math.max(0, meta.unreadCount - (wasNew ? 1 : 0)));
      setNotice({ type: 'success', text: response.message || 'پیام تماس با موفقیت حذف شد.' });
      if (isLastItemOnPage && page > 1) {
        setPage((current) => current - 1);
      } else {
        await fetchMessages({ silent: true });
      }
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'حذف پیام انجام نشد.' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="admin-contact-messages" dir="rtl">
      <div className="admin-contact-summary">
        <div><span><FiInbox /></span><div><small>کل پیام‌ها</small><strong>{Number(meta.total).toLocaleString('fa-IR')}</strong></div></div>
        <div className="is-unread"><span><FiMail /></span><div><small>نیازمند بررسی</small><strong>{Number(meta.unreadCount).toLocaleString('fa-IR')}</strong></div></div>
        <p>پیام‌های ارسال‌شده از فرم ارتباط با ما در این بخش ثبت می‌شوند.</p>
      </div>

      <div className="admin-contact-toolbar">
        <label><FiSearch aria-hidden="true" /><input value={filters.search} onChange={(event) => { setFilters((current) => ({ ...current, search: event.target.value })); setPage(1); }} placeholder="جستجو با نام، ایمیل، تلفن یا کد پیگیری..." /></label>
        <select value={filters.status} onChange={(event) => { setFilters((current) => ({ ...current, status: event.target.value })); setPage(1); }}>
          <option value="">همه وضعیت‌ها</option>
          {Object.entries(STATUSES).map(([value, info]) => <option key={value} value={value}>{info.label}</option>)}
        </select>
        <button type="button" onClick={() => fetchMessages()} aria-label="به‌روزرسانی پیام‌ها"><FiRefreshCw /></button>
      </div>

      {notice.text && <div className={`admin-contact-notice is-${notice.type}`} role={notice.type === 'error' ? 'alert' : 'status'}>{notice.text}</div>}

      <div className="admin-contact-workspace">
        <section className="admin-contact-list" aria-label="فهرست پیام‌ها">
          {loading ? <div className="admin-contact-empty">در حال دریافت پیام‌ها...</div> : items.length === 0 ? (
            <div className="admin-contact-empty"><FiInbox /><strong>پیامی یافت نشد</strong><span>با تغییر فیلترها دوباره جستجو کنید.</span></div>
          ) : items.map((item) => {
            const status = STATUSES[item.status] || STATUSES.new;
            return (
              <button key={item._id} type="button" className={`${selected?._id === item._id ? 'is-active' : ''} ${!item.readAt ? 'is-unread' : ''}`} onClick={() => openMessage(item)}>
                <span className="admin-contact-avatar"><FiUser /></span>
                <div className="admin-contact-list-copy">
                  <div><strong>{item.name}</strong><span className={`admin-contact-status is-${status.tone}`}>{status.label}</span></div>
                  <small>{TOPICS[item.topic] || TOPICS.other}</small>
                  <p>{item.message}</p>
                  <footer><span dir="ltr">{item.ticketNumber}</span><time>{formatDate(item.createdAt)}</time></footer>
                </div>
                <FiArrowLeft className="admin-contact-row-arrow" />
              </button>
            );
          })}
          {meta.pages > 1 && (
            <div className="admin-contact-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>قبلی</button>
              <span>صفحه {Number(page).toLocaleString('fa-IR')} از {Number(meta.pages).toLocaleString('fa-IR')}</span>
              <button type="button" disabled={page >= meta.pages} onClick={() => setPage((current) => current + 1)}>بعدی</button>
            </div>
          )}
        </section>

        <section className="admin-contact-detail" aria-label="جزئیات پیام انتخاب‌شده">
          {detailLoading ? <div className="admin-contact-empty">در حال دریافت جزئیات...</div> : !selected ? (
            <div className="admin-contact-empty"><FiMail /><strong>یک پیام را انتخاب کنید</strong><span>جزئیات کامل و ابزارهای پیگیری در اینجا نمایش داده می‌شود.</span></div>
          ) : (
            <>
              <header className="admin-contact-detail-header">
                <div><small dir="ltr">{selected.ticketNumber}</small><h2>{selected.name}</h2><span>{TOPICS[selected.topic] || TOPICS.other}</span></div>
                <span className={`admin-contact-status is-${(STATUSES[selected.status] || STATUSES.new).tone}`}>{(STATUSES[selected.status] || STATUSES.new).label}</span>
              </header>

              <div className="admin-contact-meta-grid">
                <a href={`mailto:${selected.email}`}><FiMail /><div><small>ایمیل</small><strong dir="ltr">{selected.email}</strong></div></a>
                {selected.phone ? <a href={`tel:${selected.phone.replace(/[^+\d]/g, '')}`}><FiPhone /><div><small>تلفن</small><strong dir="ltr">{selected.phone}</strong></div></a> : <div><FiPhone /><div><small>تلفن</small><strong>ثبت نشده</strong></div></div>}
                <div><FiClock /><div><small>تاریخ دریافت</small><strong>{formatDate(selected.createdAt)}</strong></div></div>
                <div><FiCheck /><div><small>روش تماس ترجیحی</small><strong>{CONTACT_METHODS[selected.preferredContact] || 'ایمیل'}</strong></div></div>
              </div>

              <article className="admin-contact-message-body">
                <div><span>شرح درخواست</span>{selected.budgetRange && <small>بودجه: {BUDGETS[selected.budgetRange]}</small>}</div>
                <p>{selected.message}</p>
              </article>

              <div className="admin-contact-actions">
                <label>وضعیت پیگیری<select value={editor.status} onChange={(event) => setEditor((current) => ({ ...current, status: event.target.value }))}>{Object.entries(STATUSES).map(([value, info]) => <option key={value} value={value}>{info.label}</option>)}</select></label>
                <label>یادداشت داخلی<textarea rows="5" value={editor.adminNote} onChange={(event) => setEditor((current) => ({ ...current, adminNote: event.target.value }))} placeholder="نتیجه تماس، نیازهای مهم یا اقدام بعدی را ثبت کنید..." /></label>
                <div>
                  <a href={`mailto:${selected.email}?subject=${encodeURIComponent(`پاسخ هریاد | ${selected.ticketNumber}`)}`}><FiMail /> پاسخ با ایمیل</a>
                  {selected.phone && <a href={`tel:${selected.phone.replace(/[^+\d]/g, '')}`}><FiPhone /> تماس با مشتری</a>}
                  <button
                    type="button"
                    className="admin-contact-delete-button"
                    onClick={deleteMessage}
                    disabled={deleting || saving}
                    aria-label={`حذف پیام ${selected.ticketNumber}`}
                  >
                    <FiTrash2 /> {deleting ? 'در حال حذف...' : 'حذف پیام'}
                  </button>
                  <button type="button" className="admin-contact-save-button" onClick={saveMessage} disabled={saving || deleting}><FiCheck /> {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}</button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminContactMessages;
